/**
 * SESSION 146aq — DhanSwingTab
 *
 * Admin UI for the multi-day DhanSwing engine (Phase 1+2).
 * Shows today's candidates ranked, supports manual scoring trigger,
 * and lets admin run / inspect 2yr & 5yr backtests.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Play, RefreshCw, Activity, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const Pill = ({ tone = 'neutral', children, testId }) => {
  const palette = {
    success: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    warn: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    danger: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    info: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
    neutral: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30',
    accent: 'bg-violet-500/15 text-violet-300 ring-violet-500/30',
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${palette[tone] || palette.neutral}`}
    >
      {children}
    </span>
  );
};

const SectionHeader = ({ title, sub, right }) => (
  <div className="flex items-end justify-between border-b border-zinc-800 pb-3 mb-4">
    <div>
      <h2 className="text-base font-semibold text-zinc-100">{title}</h2>
      {sub && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

const formatPct = (n) => (typeof n === 'number' ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` : '—');
const formatNum = (n, dp = 2) => (typeof n === 'number' ? n.toFixed(dp) : '—');

const compressionTone = (method) => {
  if (method === 'BOTH') return 'success';
  if (method === 'VCP') return 'info';
  if (method === 'BBW') return 'accent';
  return 'neutral';
};

const CandidateRow = ({ row, onToggle, isOpen }) => {
  const ineligibleReasons = (row.trend_template_failed_reasons || []).join(', ');
  return (
    <>
      <tr
        onClick={onToggle}
        data-testid={`swing-candidate-row-${row.symbol}`}
        className={`cursor-pointer border-b border-zinc-800 hover:bg-zinc-900/60 ${row.eligible ? '' : 'opacity-70'}`}
      >
        <td className="px-3 py-2 text-zinc-400">
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-3 py-2 font-mono font-semibold text-zinc-100">{row.symbol}</td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5">
            <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${row.score >= 75 ? 'bg-emerald-400' : row.score >= 50 ? 'bg-amber-400' : 'bg-zinc-500'}`}
                style={{ width: `${Math.min(100, Math.max(0, row.score))}%` }}
              />
            </div>
            <span className="text-zinc-200 font-mono text-xs w-10">{formatNum(row.score, 1)}</span>
          </div>
        </td>
        <td className="px-3 py-2">
          {row.eligible ? (
            <Pill tone="success" testId={`swing-eligible-${row.symbol}`}>ELIGIBLE</Pill>
          ) : (
            <Pill tone="neutral">FAIL</Pill>
          )}
        </td>
        <td className="px-3 py-2">
          {row.compression_method ? (
            <Pill tone={compressionTone(row.compression_method)}>{row.compression_method}</Pill>
          ) : <span className="text-zinc-600 text-[11px] italic" title="Neither VCP contractions nor BBW squeeze detected — no setup currently forming.">no setup</span>}
        </td>
        <td className="px-3 py-2 font-mono text-zinc-300">{formatNum(row.last_close)}</td>
        <td className="px-3 py-2 font-mono text-zinc-300">{formatNum(row.pivot_price)}</td>
        <td className="px-3 py-2 font-mono text-zinc-300">{formatNum(row.atr_14)}</td>
        <td className="px-3 py-2 font-mono text-zinc-300">{formatNum(row.rs_rating, 1)}</td>
      </tr>
      {isOpen && (
        <tr className="bg-zinc-950/60">
          <td colSpan={9} className="px-6 py-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-zinc-300">
              <div>
                <div className="text-zinc-500 mb-1">Compression Inputs</div>
                <div className="font-mono">
                  contractions: {row.compression_score_inputs?.contractions ?? '—'}<br />
                  last_pct: {row.compression_score_inputs?.last_contraction_pct ?? '—'}%<br />
                  vol_dryup: {row.compression_score_inputs?.volume_dryup ?? '—'}<br />
                  bbw_pct: {row.compression_score_inputs?.bbw_percentile ?? '—'}<br />
                  base_days: {row.compression_score_inputs?.base_length_days ?? '—'}
                </div>
              </div>
              <div>
                <div className="text-zinc-500 mb-1">Trend Template</div>
                <div className="font-mono">
                  passed: {String(row.trend_template_passed)}<br />
                  {ineligibleReasons && (
                    <span className="text-rose-300 break-words">failed: {ineligibleReasons}</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-zinc-500 mb-1">Levels</div>
                <div className="font-mono">
                  pivot: {formatNum(row.pivot_price)}<br />
                  base_low: {formatNum(row.base_low)}<br />
                  atr14: {formatNum(row.atr_14)}
                </div>
              </div>
              <div>
                <div className="text-zinc-500 mb-1">Notes</div>
                <div className="font-mono">{(row.notes || []).join(', ') || '—'}</div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const CandidatesPanel = ({ getAuth }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [openSym, setOpenSym] = useState(null);
  const [status, setStatus] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuth();
      const [c, s] = await Promise.all([
        axios.get(`${API}/api/dhan-swing/candidates?limit=50&eligible_only=${eligibleOnly}`, { headers }),
        axios.get(`${API}/api/dhan-swing/status`, { headers }),
      ]);
      setRows(c.data?.candidates || []);
      setStatus(s.data || null);
    } catch (e) {
      toast.error(`Failed to load candidates: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [eligibleOnly, getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const triggerScoring = async () => {
    try {
      const headers = getAuth();
      const res = await axios.post(`${API}/api/dhan-swing/score/run`, {}, { headers });
      toast.success(res.data?.message || 'Scoring run triggered.');
      // Poll status every 10s for up to 10 min.
      let polled = 0;
      const id = setInterval(async () => {
        polled++;
        try {
          const r = await axios.get(`${API}/api/dhan-swing/status`, { headers });
          setStatus(r.data);
          if (!r.data?.scoring_in_progress || polled > 60) {
            clearInterval(id);
            refresh();
          }
        } catch { /* ignore */ }
      }, 10000);
    } catch (e) {
      toast.error(`Failed to trigger: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const lastRun = status?.last_run;

  return (
    <div data-testid="swing-candidates-panel">
      <SectionHeader
        title="Today's Candidates"
        sub="Nifty 200 spot equity scored against Trend Template + VCP/BBW compression."
        right={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-zinc-400">
              <input
                type="checkbox"
                checked={eligibleOnly}
                onChange={(e) => setEligibleOnly(e.target.checked)}
                data-testid="swing-eligible-only-toggle"
              />
              Eligible only
            </label>
            <button
              onClick={refresh}
              disabled={loading}
              className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="swing-refresh-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
              Refresh
            </button>
            <button
              onClick={triggerScoring}
              disabled={status?.scoring_in_progress}
              className="rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-1.5 text-xs text-white inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="swing-trigger-scoring-btn"
            >
              {status?.scoring_in_progress
                ? <><Loader2 className="animate-spin" size={12} /> Running…</>
                : <><Play size={12} /> Run Scoring</>}
            </button>
          </div>
        }
      />

      {/* Inline legend — explains every state at a glance */}
      <div className="mb-4 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3" data-testid="swing-legend">
        <div className="flex items-start gap-2 text-[11px] text-zinc-400">
          <Info size={13} className="text-violet-300 mt-0.5 shrink-0" />
          <div className="space-y-1.5 leading-relaxed">
            <div>
              <span className="text-zinc-200 font-semibold">ELIGIBLE</span> = passed BOTH gates: Minervini Trend Template (8 criteria) AND a compression pattern detected.
              {' '}<span className="text-zinc-200 font-semibold">FAIL</span> = at least one gate failed. Click the <span className="text-zinc-200">▸</span> chevron to see which criteria failed for any row.
            </div>
            <div>
              <span className="text-sky-300 font-semibold">VCP</span> = classical Minervini contractions (each tighter than the last). {' '}
              <span className="text-violet-300 font-semibold">BBW</span> = Bollinger Band Width in lowest 10% of last 500 bars (squeeze). {' '}
              <span className="text-emerald-300 font-semibold">BOTH</span> = strongest signal. {' '}
              <span className="text-zinc-500 font-semibold">—</span> = no setup currently forming.
            </div>
            <div>
              <span className="text-zinc-200 font-semibold">SCORE</span> 0-100 composite. <span className="text-emerald-300">≥75</span> high conviction · <span className="text-amber-300">50-74</span> watch · <span className="text-zinc-500">&lt;50</span> partial. {' '}
              <span className="text-zinc-200 font-semibold">PIVOT</span> = the price you'd buy a breakout above (0.00 means no valid pivot yet).
            </div>
            <div className="pt-1 border-t border-zinc-800/60 text-zinc-500">
              <span className="text-zinc-400 font-semibold">Data freshness:</span> Scoring uses <span className="text-zinc-300">daily OHLC bars</span>. During market hours
              (09:15–15:30 IST) the most-recent bar = yesterday's close.
              <span className="text-emerald-300"> The SIGNAL acts on today's open</span> — if pivot holds + 1.2× volume, you'd buy the breakout. Run the
              scheduled scoring at <span className="text-zinc-300">16:30 IST</span> (post-market) to score against today's freshly-closed bar.
            </div>
          </div>
        </div>
      </div>

      {lastRun && (
        <div className="mb-3 text-xs text-zinc-400 flex items-center gap-3 flex-wrap" data-testid="swing-last-run-summary">
          <Pill tone="info">RUN {lastRun.run_id}</Pill>
          <span>scored: <span className="text-zinc-200">{lastRun.scored}</span></span>
          <span>eligible: <span className="text-emerald-300">{lastRun.eligible}</span></span>
          <span>universe: <span className="text-zinc-200">{lastRun.universe_size}</span></span>
          <span>errors: <span className={lastRun.errors > 0 ? 'text-amber-300' : 'text-zinc-300'}>{lastRun.errors}</span></span>
          <span>finished: <span className="text-zinc-300">{lastRun.finished_at}</span></span>
        </div>
      )}

      {!lastRun && !loading && (
        <div className="rounded-lg border border-dashed border-zinc-700 px-4 py-6 text-center text-sm text-zinc-400 mb-3" data-testid="swing-no-runs-yet">
          No scoring runs yet. Click <span className="text-violet-300">Run Scoring</span> to populate candidates.
          A run takes ~3-7 minutes (Dhan rate limits).
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2 text-left">Symbol</th>
              <th className="px-3 py-2 text-left">Score</th>
              <th className="px-3 py-2 text-left">Eligible</th>
              <th className="px-3 py-2 text-left">Compression</th>
              <th className="px-3 py-2 text-left">Close</th>
              <th className="px-3 py-2 text-left">Pivot</th>
              <th className="px-3 py-2 text-left">ATR14</th>
              <th className="px-3 py-2 text-left">RS</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-zinc-500" data-testid="swing-empty-row">No candidates yet.</td></tr>
            )}
            {rows.map((r) => (
              <CandidateRow
                key={r.symbol}
                row={r}
                isOpen={openSym === r.symbol}
                onToggle={() => setOpenSym(openSym === r.symbol ? null : r.symbol)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BacktestPanel = ({ getAuth }) => {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [period, setPeriod] = useState(2.0);
  const [scoreThreshold, setScoreThreshold] = useState(75);
  const [topNPerDay, setTopNPerDay] = useState(5);
  const [maxHoldingDays, setMaxHoldingDays] = useState(30);
  const [volMultiplier, setVolMultiplier] = useState(1.2);
  const [showTrades, setShowTrades] = useState(true);
  const [history, setHistory] = useState([]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuth();
      const [latestRes, runsRes] = await Promise.all([
        axios.get(`${API}/api/dhan-swing/backtest/latest`, { headers }),
        axios.get(`${API}/api/dhan-swing/backtest/runs?limit=10`, { headers }),
      ]);
      setLatest(latestRes.data?.result || null);
      setRunning(!!latestRes.data?.is_running);
      setHistory(runsRes.data?.runs || []);
    } catch (e) {
      toast.error(`Failed to load backtests: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const triggerBacktest = async () => {
    try {
      const headers = getAuth();
      const res = await axios.post(
        `${API}/api/dhan-swing/backtest/run`,
        {
          period_years: Number(period),
          score_threshold: Number(scoreThreshold),
          top_n_per_day: Number(topNPerDay),
          max_holding_days: Number(maxHoldingDays),
          vol_multiplier: Number(volMultiplier),
        },
        { headers },
      );
      toast.success(res.data?.message || 'Backtest started.');
      setRunning(true);
      // Poll every 30s.
      let polled = 0;
      const id = setInterval(async () => {
        polled++;
        try {
          const r = await axios.get(`${API}/api/dhan-swing/backtest/latest`, { headers });
          setLatest(r.data?.result || null);
          setRunning(!!r.data?.is_running);
          if (!r.data?.is_running || polled > 80) {
            clearInterval(id);
            refresh();
          }
        } catch { /* ignore */ }
      }, 30000);
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div data-testid="swing-backtest-panel" className="mt-10">
      <SectionHeader
        title="Backtest"
        sub="Day-by-day historical replay across Nifty 200 — no look-ahead. ~5-15 min per run."
        right={
          <button
            onClick={refresh}
            disabled={loading}
            className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="swing-bt-refresh-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Period (years)
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200"
            data-testid="swing-bt-period-select"
          >
            <option value={2.0}>2 years</option>
            <option value={5.0}>5 years</option>
          </select>
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Score ≥
          <input
            type="number"
            value={scoreThreshold}
            onChange={(e) => setScoreThreshold(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200"
            data-testid="swing-bt-score-threshold"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Top N per day
          <input
            type="number"
            value={topNPerDay}
            onChange={(e) => setTopNPerDay(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200"
            data-testid="swing-bt-top-n"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Max holding (days)
          <input
            type="number"
            value={maxHoldingDays}
            onChange={(e) => setMaxHoldingDays(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200"
            data-testid="swing-bt-max-holding"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="Breakout-day volume must exceed this multiple of the prior 30-day average. Lower = more trades, lower quality.">
          Volume × (breakout)
          <input
            type="number"
            step="0.1"
            value={volMultiplier}
            onChange={(e) => setVolMultiplier(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200"
            data-testid="swing-bt-vol-multiplier"
          />
        </label>
        <button
          onClick={triggerBacktest}
          disabled={running}
          className="self-end rounded-md bg-emerald-600 hover:bg-emerald-500 px-3 py-2 text-xs text-white inline-flex items-center justify-center gap-1 disabled:opacity-50"
          data-testid="swing-bt-run-btn"
        >
          {running
            ? <><Loader2 className="animate-spin" size={12} /> Running…</>
            : <><Play size={12} /> Run Backtest</>}
        </button>
      </div>

      {latest ? (
        <div className="rounded-lg border border-zinc-800 p-4 bg-zinc-950" data-testid="swing-bt-latest">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-zinc-500">{latest.backtest_id} · {latest.status}</div>
              <div className="text-sm text-zinc-200 mt-1">
                {latest.period_from} → {latest.period_to} · universe {latest.fetched_universe_size}/{latest.universe_size}
              </div>
            </div>
            <Pill tone={latest.status === 'completed' ? 'success' : latest.status === 'running' ? 'info' : 'danger'}>
              {latest.status?.toUpperCase()}
            </Pill>
          </div>
          {latest.status === 'completed' && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
              <Stat label="Trades" value={latest.total_trades} />
              <Stat label="Win Rate" value={formatPct(latest.win_rate_pct)} />
              <Stat label="Avg Winner" value={formatPct(latest.avg_winner_pct)} tone={latest.avg_winner_pct > 0 ? 'success' : 'neutral'} />
              <Stat label="Avg Loser" value={formatPct(latest.avg_loser_pct)} tone={latest.avg_loser_pct < 0 ? 'danger' : 'neutral'} />
              <Stat label="Expectancy" value={formatPct(latest.expectancy_pct)} tone={latest.expectancy_pct > 0 ? 'success' : 'danger'} />
              <Stat label="Max DD" value={formatPct(-latest.max_drawdown_pct)} tone={latest.max_drawdown_pct < 10 ? 'success' : 'warn'} />
              <Stat label="Sharpe" value={formatNum(latest.sharpe, 2)} />
            </div>
          )}
          {latest.by_exit_reason && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {Object.entries(latest.by_exit_reason).map(([k, v]) => (
                <Pill key={k} tone={k === 'T1' || k === 'T2' ? 'success' : k === 'SL' ? 'danger' : 'neutral'}>
                  {k}: {v}
                </Pill>
              ))}
            </div>
          )}
          {latest.error && (
            <div className="mt-2 text-rose-300 text-xs font-mono">{latest.error}</div>
          )}

          {/* Tuning hint when trade count is low */}
          {latest.status === 'completed' && latest.total_trades > 0 && latest.total_trades < 25 && (
            <div className="mt-3 rounded-md border border-amber-700/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200/90 leading-relaxed" data-testid="swing-bt-low-trade-hint">
              <span className="font-semibold">Why so few trades?</span> Strict VCP is supposed to be selective — Minervini's classic setup typically fires 3–6×/year per name.
              To see more trades, try lowering <span className="font-mono">Score≥</span> to 65–70 or <span className="font-mono">Volume×</span> to 1.2.
              <span className="text-zinc-400"> Watch the Win Rate — if it stays ≥55% with more trades, the looser threshold is genuinely better.</span>
            </div>
          )}

          {/* Per-trade list */}
          {latest.status === 'completed' && Array.isArray(latest.trades) && latest.trades.length > 0 && (
            <PortfolioSimulator trades={latest.trades} />
          )}

          {/* Per-trade list */}
          {latest.status === 'completed' && Array.isArray(latest.trades) && latest.trades.length > 0 && (
            <div className="mt-4 border-t border-zinc-800 pt-3">
              <button
                onClick={() => setShowTrades(!showTrades)}
                className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
                data-testid="swing-bt-toggle-trades"
              >
                {showTrades ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                Trades ({latest.trades.length}) — which stocks fired this backtest
              </button>
              {showTrades && (
                <div className="mt-2 overflow-x-auto rounded-lg border border-zinc-800">
                  <table className="w-full text-[11px]" data-testid="swing-bt-trades-table">
                    <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider">
                      <tr>
                        <th className="px-3 py-2 text-left">Symbol</th>
                        <th className="px-3 py-2 text-left">Entry Date</th>
                        <th className="px-3 py-2 text-right">Entry</th>
                        <th className="px-3 py-2 text-left">Exit Date</th>
                        <th className="px-3 py-2 text-right">Exit</th>
                        <th className="px-3 py-2 text-left">Reason</th>
                        <th className="px-3 py-2 text-right">PnL %</th>
                        <th className="px-3 py-2 text-right">Bars</th>
                        <th className="px-3 py-2 text-right">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...latest.trades]
                        .sort((a, b) => (b.pnl_pct ?? 0) - (a.pnl_pct ?? 0))
                        .map((t, i) => (
                          <tr key={`${t.symbol}-${t.entry_date}-${i}`} className="border-b border-zinc-800/60" data-testid={`swing-bt-trade-${t.symbol}-${i}`}>
                            <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{t.symbol}</td>
                            <td className="px-3 py-1.5 text-zinc-400 font-mono">{t.entry_date}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{formatNum(t.entry_price)}</td>
                            <td className="px-3 py-1.5 text-zinc-400 font-mono">{t.exit_date || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{formatNum(t.exit_price)}</td>
                            <td className="px-3 py-1.5">
                              <Pill tone={t.exit_reason === 'T1' || t.exit_reason === 'T2' ? 'success' : t.exit_reason === 'SL' ? 'danger' : 'neutral'}>
                                {t.exit_reason}
                              </Pill>
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono font-semibold ${t.pnl_pct > 0 ? 'text-emerald-300' : t.pnl_pct < 0 ? 'text-rose-300' : 'text-zinc-300'}`}>
                              {formatPct(t.pnl_pct)}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{t.bars_held}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{formatNum(t.score, 1)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-zinc-700 px-4 py-6 text-center text-sm text-zinc-400" data-testid="swing-bt-no-runs">
          No backtests yet. Set parameters above and click <span className="text-emerald-300">Run Backtest</span>.
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-zinc-500 mb-2">Recent runs</div>
          <div className="overflow-x-auto rounded-lg border border-zinc-800">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Run ID</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-left">Trades</th>
                  <th className="px-3 py-2 text-left">Win%</th>
                  <th className="px-3 py-2 text-left">Expectancy</th>
                  <th className="px-3 py-2 text-left">Max DD</th>
                  <th className="px-3 py-2 text-left">Started</th>
                </tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r.backtest_id} className="border-b border-zinc-800" data-testid={`swing-bt-row-${r.backtest_id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-300">{r.backtest_id}</td>
                    <td className="px-3 py-2">
                      <Pill tone={r.status === 'completed' ? 'success' : r.status === 'running' ? 'info' : 'danger'}>
                        {r.status}
                      </Pill>
                    </td>
                    <td className="px-3 py-2 text-zinc-300">{r.period_years}y</td>
                    <td className="px-3 py-2 font-mono text-zinc-200">{r.total_trades ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-zinc-200">{formatPct(r.win_rate_pct)}</td>
                    <td className="px-3 py-2 font-mono text-zinc-200">{formatPct(r.expectancy_pct)}</td>
                    <td className="px-3 py-2 font-mono text-zinc-200">{formatPct(-r.max_drawdown_pct)}</td>
                    <td className="px-3 py-2 text-zinc-400">{r.started_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const PortfolioSimulator = ({ trades }) => {
  const [capitalLakhs, setCapitalLakhs] = useState(10);
  const [slots, setSlots] = useState(5);

  const sim = useMemo(() => {
    const startCapital = capitalLakhs * 100000;
    const N = trades.length;
    if (N === 0) return null;

    // Method A — equal allocation, no compounding (simplest mental model).
    const perTrade = startCapital / N;
    const totalPnl = trades.reduce((s, t) => s + (perTrade * (t.pnl_pct || 0)) / 100, 0);
    const aFinal = startCapital + totalPnl;
    const aPct = (aFinal / startCapital - 1) * 100;

    // Method B — sequential all-in compounding (one trade at a time).
    let bEq = startCapital;
    const sortedByExit = [...trades]
      .filter(t => t.exit_date)
      .sort((a, b) => (a.exit_date || '').localeCompare(b.exit_date || ''));
    for (const t of sortedByExit) {
      bEq *= 1 + (t.pnl_pct || 0) / 100;
    }
    const bFinal = bEq;
    const bPct = (bFinal / startCapital - 1) * 100;

    // Method C — N-slot concurrent portfolio (realistic).
    // Each slot starts with capital/slots. Trades fire in entry-date order.
    // A trade takes the next available slot at its full capital, returns
    // capital × (1+pnl/100) to that slot on exit.
    const slotCap = startCapital / slots;
    const pool = Array.from({ length: slots }, () => slotCap);
    const events = [];
    for (let i = 0; i < trades.length; i++) {
      const t = trades[i];
      if (!t.entry_date || !t.exit_date) continue;
      events.push({ type: 'entry', date: t.entry_date, idx: i, pnl: t.pnl_pct || 0 });
      events.push({ type: 'exit', date: t.exit_date, idx: i, pnl: t.pnl_pct || 0 });
    }
    events.sort((a, b) => {
      if (a.date === b.date) return a.type === 'exit' ? -1 : 1; // exits before entries same day
      return a.date.localeCompare(b.date);
    });
    const slotByTrade = {};
    let skipped = 0;
    for (const ev of events) {
      if (ev.type === 'entry') {
        const free = pool.findIndex((cap, idx) =>
          !Object.values(slotByTrade).includes(idx)
        );
        if (free === -1) { skipped++; continue; }
        slotByTrade[ev.idx] = free;
      } else { // exit
        const slot = slotByTrade[ev.idx];
        if (slot === undefined) continue;
        pool[slot] = pool[slot] * (1 + ev.pnl / 100);
        delete slotByTrade[ev.idx];
      }
    }
    const cFinal = pool.reduce((s, c) => s + c, 0);
    const cPct = (cFinal / startCapital - 1) * 100;

    return {
      perTrade,
      slotCap,
      skipped,
      taken: N - skipped,
      a: { final: aFinal, pct: aPct },
      b: { final: bFinal, pct: bPct },
      c: { final: cFinal, pct: cPct },
    };
  }, [trades, capitalLakhs, slots]);

  if (!sim) return null;

  const fmtINR = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    return `₹${Math.round(n).toLocaleString('en-IN')}`;
  };

  return (
    <div className="mt-4 border-t border-zinc-800 pt-3" data-testid="swing-bt-portfolio-sim">
      <div className="text-xs text-zinc-300 font-semibold mb-2 flex items-center gap-2">
        Portfolio Simulator
        <span className="text-[10px] font-normal text-zinc-500">— "If I had deployed this much in equal proportion…"</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-zinc-400 mb-3">
        <label className="flex items-center gap-1.5">
          Capital (₹ Lakhs)
          <input
            type="number"
            value={capitalLakhs}
            onChange={(e) => setCapitalLakhs(Math.max(0.1, Number(e.target.value)))}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 w-20 text-zinc-200"
            data-testid="swing-bt-portfolio-capital"
          />
        </label>
        <label className="flex items-center gap-1.5">
          Concurrent slots
          <input
            type="number"
            value={slots}
            min={1}
            max={20}
            onChange={(e) => setSlots(Math.max(1, Math.min(20, Number(e.target.value))))}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-0.5 w-16 text-zinc-200"
            data-testid="swing-bt-portfolio-slots"
          />
        </label>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <SimCard
          label="A · Equal allocation, no compound"
          sub={`₹${Math.round(sim.perTrade).toLocaleString('en-IN')} × ${trades.length} trades`}
          final={sim.a.final}
          pct={sim.a.pct}
          fmt={fmtINR}
          testId="swing-bt-sim-equal"
        />
        <SimCard
          label="B · Sequential all-in compound"
          sub="Reinvest 100% on each trade, in series (theoretical max)"
          final={sim.b.final}
          pct={sim.b.pct}
          fmt={fmtINR}
          testId="swing-bt-sim-compound"
        />
        <SimCard
          label={`C · ${slots}-slot concurrent (realistic)`}
          sub={`₹${Math.round(sim.slotCap).toLocaleString('en-IN')}/slot · ${sim.taken} taken${sim.skipped ? `, ${sim.skipped} skipped (slots full)` : ''}`}
          final={sim.c.final}
          pct={sim.c.pct}
          fmt={fmtINR}
          testId="swing-bt-sim-portfolio"
          highlight
        />
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
        ⚠ Simulator is gross of brokerage / STT / GST / slippage. Real net ≈ 0.5–1% lower per trade. {' '}
        Method <span className="text-emerald-300">C</span> is the most realistic (matches how you'd actually trade with N positions).
      </p>
    </div>
  );
};

const SimCard = ({ label, sub, final, pct, fmt, testId, highlight }) => (
  <div
    className={`rounded-md border p-3 ${highlight ? 'border-emerald-700/50 bg-emerald-950/20' : 'border-zinc-800 bg-zinc-950'}`}
    data-testid={testId}
  >
    <div className="text-[11px] text-zinc-400">{label}</div>
    <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    <div className={`text-lg font-bold mt-1.5 ${pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
      {fmt(final)}
    </div>
    <div className={`text-xs mt-0.5 ${pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
      {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
    </div>
  </div>
);

const Stat = ({ label, value, tone = 'neutral' }) => {
  const colors = {
    success: 'text-emerald-300',
    danger: 'text-rose-300',
    warn: 'text-amber-300',
    info: 'text-sky-300',
    neutral: 'text-zinc-200',
  };
  return (
    <div className="rounded-md bg-zinc-900 border border-zinc-800 px-3 py-2">
      <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${colors[tone] || colors.neutral}`}>{value}</div>
    </div>
  );
};

const DhanSwingTab = () => {
  const getAuth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  return (
    <div className="space-y-4 px-2 md:px-4 pb-12" data-testid="dhan-swing-tab">
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Activity size={14} className="text-violet-300" />
        <span>DhanSwing — Multi-day VCP swing trading on Nifty 200 (Phase 1+2: scoring + backtest, no live execution).</span>
      </div>

      <CandidatesPanel getAuth={getAuth} />
      <BacktestPanel getAuth={getAuth} />
    </div>
  );
};

export default DhanSwingTab;
